import { useEffect, useRef } from "react";
import {
  validateUsername as validateUsernameUtil,
  sanitizeUsername,
} from "@/utils/usernameValidation";
import { useUpdateUserProfileMutation } from "@/api/clubsApi";
import { Icon } from "@/components/ui/Icon";
import { cn } from "@/components/ui/cn";

const inputClassName = cn(
  "w-full rounded-2xl border border-border-tertiary p-3",
  "text-base text-text-primary bg-input-background",
  "placeholder:text-text-tertiary",
  "focus:outline-none focus:ring-2 focus:ring-text-primary focus:ring-offset-2 focus:ring-offset-background",
  "focus:border-text-primary"
);

interface OnboardingBasicInfoStepProps {
  firstName: string;
  setFirstName: (value: string) => void;
  lastName: string;
  setLastName: (value: string) => void;
  username: string;
  setUsername: (value: string) => void;
  usernameError: string | null;
  setUsernameError: (error: string | null) => void;
  userId: string;
  onContinue: () => void;
}

export function OnboardingBasicInfoStep({
  firstName,
  setFirstName,
  lastName,
  setLastName,
  username,
  setUsername,
  usernameError,
  setUsernameError,
  userId,
  onContinue,
}: OnboardingBasicInfoStepProps) {
  const userIdRef = useRef(userId);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Debounced username validation
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const validationVersionRef = useRef(0);

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    validationVersionRef.current += 1;
    const thisVersion = validationVersionRef.current;

    if (!username.trim()) {
      setUsernameError(null);
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      const error = await validateUsernameUtil(
        username.trim(),
        userIdRef.current || undefined
      );
      if (validationVersionRef.current === thisVersion) {
        setUsernameError(error);
      }
    }, 300);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [username, setUsernameError]);

  const isValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    username.trim().length > 0 &&
    !usernameError;

  const [updateProfile, { isLoading: isSaving }] =
    useUpdateUserProfileMutation();

  const handleContinue = async () => {
    if (!isValid || !userId) return;

    try {
      await updateProfile({
        id: userId,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        username: sanitizeUsername(username).trim(),
      }).unwrap();
      onContinue();
    } catch (error) {
      console.error("[OnboardingBasicInfo] Save failed:", error);
      window.alert("Failed to save your information. Please try again.");
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto autohide-scrollbar px-8 pt-4 pb-8">
        <div className="flex flex-col gap-10">
          <h1 className="text-h1 text-text-primary">Your basic info</h1>

          {/* Form fields */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-body-2 text-text-secondary font-medium">
                First name
              </label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={inputClassName}
                autoCapitalize="words"
                autoCorrect="off"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-body-2 text-text-secondary font-medium">
                Last name
              </label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={inputClassName}
                autoCapitalize="words"
                autoCorrect="off"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-body-2 text-text-secondary font-medium">
                Username
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base text-text-tertiary">
                  @
                </span>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={cn(inputClassName, "pl-7")}
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="off"
                  maxLength={30}
                />
              </div>
              {usernameError ? (
                <p className="text-body-3 text-red-500">{usernameError}</p>
              ) : (
                <p className="text-body-3 text-text-tertiary">
                  4+ characters · letters, numbers, dashes
                </p>
              )}
            </div>
          </div>

          {/* Info banner */}
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-surface">
            <Icon name="eyes" size={16} className="text-text-secondary mt-0.5 flex-shrink-0" />
            <p className="text-body-2 text-text-secondary">
              After setup, make your profile yours — add your interests,
              pronouns, zodiac sign, and more anytime.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 px-8 pb-6 pt-4">
        <button
          onClick={handleContinue}
          disabled={!isValid || isSaving}
          className={cn(
            "w-full py-3.5 rounded-full text-body-1-medium font-medium transition-opacity",
            isValid && !isSaving
              ? "bg-text-primary text-text-reversed hover:opacity-90"
              : "bg-border-tertiary text-text-tertiary cursor-not-allowed"
          )}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
